import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { NotificationHandlerComponent } from '@ebootis/utilities';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationService } from 'projects/zbs-app/src/app/components/configuration-select/configuration.service';
import { ErrorCodesI18n } from 'projects/zbs-app/src/app/i18n/ErrorCodesI18n';
import { ArticleDetailsByOid } from 'projects/zbs-app/src/app/services/Ebootis.Queries/ArticleDetailsByOid';
import { ArticleSearch } from 'projects/zbs-app/src/app/services/Ebootis.Queries/ArticleSearch';
import { LobByOid } from 'projects/zbs-app/src/app/services/Ebootis.Queries/LobByOid';
import { ArticleBasketService } from 'projects/zbs-app/src/app/services/article-basket.service';
import { Subject, concat } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ArticleService } from '../../article.service';
import { ArticleCompareI18n } from './ArticleCompareI18n';

@Component({
	selector: 'app-article-compare',
	templateUrl: './article-compare.component.html',
	styleUrls: ['./article-compare.component.scss'],
	providers: [ArticleCompareI18n, ErrorCodesI18n],
})
export class ArticleCompareComponent implements OnInit, OnDestroy {

	private m_selectedArticleDetails: ArticleDetailsByOid.Result;
	private m_selectedConfigOid: string;
	private m_subscriptionsSubject = new Subject();
	public m_selectedArticle: ArticleDetailsByOid.Result;
	public m_displayMessage: string;
	public m_messageHandlerClass: string = 'info';
	public m_articles: ArticleDetailsByOid.Result[] = []; // todo DIESER TYP IST HIER FEHL AM PLATZ!
	public m_dataSource: MatTableDataSource<ArticleSearch.Article>;
	public m_displayedColumns: string[] = [];

	public m_onlyAvailableStock: boolean;
	/** VK-Preis anzeigen? */
	public m_showSalesPrice: boolean = false;
	/** Lagerverpackung anzeigen? */
	public m_showStoragePackaging: boolean = false;
	/** Unterverpackung anzeigen? */
	public m_showSubPackaging: boolean = false;

	public m_articleSelForm = this.formBuilder.group({
		article: ['', Validators.required],
	});

	constructor(
		private formBuilder: UntypedFormBuilder,
		private m_articleService: ArticleService,
		private m_basketService: ArticleBasketService,
		private changeDetectorRef: ChangeDetectorRef,
		private m_configurationService: ConfigurationService,
		private m_snackBar: MatSnackBar,
		private m_translation: ArticleCompareI18n,
		private m_errorTranslation: ErrorCodesI18n,
		private translateService: TranslateService
	) { }

	/**
	 * Die Artikel im Artikelvergleich und die Konfigurations OID werden geladen. Die Tabelle wird reinitialisiert.
	 */
	public ngOnInit(): void {
		let articleOids = [...this.m_articleService.getArticleCompare()];
		concat(...articleOids.map(oid => {
			return this.m_articleService.getArticleDetailsByOid(oid);
		})).subscribe({
			next: (res) => {
				this.m_articles.push(res);
			},
			complete: () => {
				this.initAndReinitTable();
			}
		});

		this.m_selectedConfigOid = this.m_configurationService.SelectedConfigOid;
		this.m_configurationService.getConfiguration().pipe(takeUntil(this.m_subscriptionsSubject)).subscribe({
			next: (config) => {
				this.m_onlyAvailableStock = config?.onlyAvailableStock;
				this.m_showSalesPrice = config?.showSalesPrice;
				this.m_showStoragePackaging = config?.showStoragePackaging;
				this.m_showSubPackaging = config?.showSubPackaging;
			}
		});
	}

	/**
	 * Die Subscriptions werden geschlossen.
	 */
	public ngOnDestroy(): void {
		this.m_subscriptionsSubject.next(null);
		this.m_subscriptionsSubject.complete();
	}

	/**
	 * Artikeldetails des Artikels werden geladen. Falls es weniger als 5 Artikel in dem Artikelvergleich gibt, wird der Artikel hinzugefügt.
	 * @param p_article Artikel aus der Artikelsuche
	 */
	public addArticle(p_article: ArticleDetailsByOid.Result): void {
		this.m_selectedArticle = p_article;
		if (this.m_selectedArticle) {
			this.m_articleService.getArticleDetailsByOid(this.m_selectedArticle.oid).pipe(
				takeUntil(this.m_subscriptionsSubject),
			).subscribe({
				next: (res) => {
					this.m_selectedArticleDetails = res;
					if (this.m_selectedArticleDetails) {
						if (this.m_articles.length < 5) {
							if (!this.m_articles.find((item) => item.oid == this.m_selectedArticle.oid)) {
								this.m_articles.push(this.m_selectedArticleDetails);
								this.setArticleCompare();
								this.initAndReinitTable();
								this.getControl('article').reset();
							} else {
								this.m_displayMessage = this.m_translation.articleCompareDoubleText;
								this.m_messageHandlerClass = 'danger';
								this.getControl('article').reset();
							}
						} else {
							this.m_displayMessage = this.m_translation.articleCompareLengthText;
							this.m_messageHandlerClass = 'danger';
							this.getControl('article').reset();
						}
					}
				},
			});
		}
	}

	private setArticleCompare() {
		let articleOids = new Array<string>();
		this.m_articles.forEach(article => {
			articleOids.push(article.oid);
		});
		this.m_articleService.setArticleCompare(articleOids);
	}

	/**
	 * Entfernt einen Artikel an dem angegebenen Index.
	 * @param p_index Index des Artikels im Array
	 */
	public removeArticle_Click(p_index: number): void {
		this.m_articles.splice(p_index, 1);
		this.setArticleCompare();
		this.initAndReinitTable();
	}

	/**
	 * Fügt einen Artikel auf Basis des angebenen Indexes in den Warenkorb. Im Fehlerfall gibt die aufgrufene Methode einen Fehlercode zurück.
	 * @param p_index Index des Artikels im Array
	 */
	public async articleAddToBasket_Click(p_index: number): Promise<void> {
		const basketArticle: ArticleDetailsByOid.Result = this.m_articles[p_index];
		await this.m_basketService.saveBasketItem(basketArticle.oid, this.m_selectedConfigOid, basketArticle.salesAmountMin)
			.then(() => {
				this.m_snackBar.openFromComponent(NotificationHandlerComponent, {
					data: {
						message: this.m_translation.globalBasketAddItemSuccess,
						type: 'success',
					},
					duration: 2000,
				});
			})
			.catch((error: Error) => {
				this.m_snackBar.openFromComponent(NotificationHandlerComponent, {
					data: {
						message: error.message,
						type: 'danger',
					},
					duration: 2000,
				});
			});
	}

	/**
	 * Liefert eine URL für ein Bild eines Artikels zurück.
	 * @param p_oid Article OID
	 * @returns die URL des ausgewählten Artikels
	 */
	public imageUrl(p_oid: string): string {
		return this.m_articleService.getArticleImageUrl(p_oid);
	}

	/**
	 * Gibt ein FormControl basierend auf dessen Name zurück.
	 * @param p_name FormControlName
	 */
	public getControl(p_name: string): AbstractControl {
		return this.m_articleSelForm.get(p_name);
	}

	/**
	 * Prüft, ob der Artikel in den Einkaufswagen
	 * gelegt werden kann.
	 */
	public canArticleGetAddedToBasket(p_index: number): boolean {
		const article: ArticleDetailsByOid.Result = this.m_articles[p_index];
		return this.m_articleService.canArticleGetAddedToBasket(this.m_onlyAvailableStock, article);

	}
	/**
	 * Initialisiert oder Reinitialisiert eine Tabelle und ruft Methode zum Umstellen der Tabelle auf.
	 */
	public initAndReinitTable(): void {
		let propertyLabels = [
			{ label: 'REMOVE', property: 'delete' },
			{ label: 'ARTICLE_IMAGE', property: 'image' },
			{ label: 'ARTICLE_NUMBER', property: 'id' },
			{ label: 'DESCRIPTION', property: 'descrLong' },
			{ label: 'SALES_PRICE', property: 'price' },
			{ label: 'STOCK_PACKAGING', property: 'storagePackaging' },
			{ label: 'SUB_PACKAGING', property: 'subPackaging' },
			{ label: 'AVAILABLE_STOCK', property: 'stock' },
			{ label: 'CART', property: 'basket' },
		];
		this.translateService.get(['REMOVE', 'ARTICLE_IMAGE', 'ARTICLE_NUMBER', 'DESCRIPTION', 'SALES_PRICE', 'STOCK_PACKAGING', 'SUB_PACKAGING', 'AVAILABLE_STOCK', 'CART'])
			.pipe(take(1))
			.subscribe({
				next: (values) => {
					let transposedData = [];
					for (let column = 0; column < propertyLabels.length; column++) {
						transposedData[column] = {
							label: values[propertyLabels[column].label],
							index: column,
							name: propertyLabels[column].label
						};

						for (let row = 0; row < this.m_articles.length; row++) {
							const currentArticle = this.m_articles[row];
							const currentProperty = propertyLabels[column].property;

							if (column == 0) {
								transposedData[column][`column${row}`] = row;
							} else {
								transposedData[column][`column${row}`] = currentArticle[currentProperty];
							}
						}
					}

					if (!this.m_showSalesPrice) {
						transposedData = transposedData.filter((data) => data.name !== 'SALES_PRICE');
					}
					if (!this.m_showStoragePackaging)
						transposedData = transposedData.filter((data) => data.name !== 'STOCK_PACKAGING');
					if (!this.m_showSubPackaging)
						transposedData = transposedData.filter((data) => data.name !== 'SUB_PACKAGING');

					this.m_dataSource = new MatTableDataSource(transposedData);
					this.fillLabels();
					this.changeDetectorRef.detectChanges();
				}
			});
	}

	/**
	 * Bezeichnungen werden für Querformat umgestellt.
	 */
	public fillLabels(): void {
		this.m_displayedColumns = ['label'];
		for (let i = 0; i < this.m_articles.length; i++) {
			this.m_displayedColumns.push('column' + i);
		}
	}

	/**
	 * Die Nachrichten des MessageHandlers werden zurückgesetzt.
	 */
	public clearDisplayMessages(): void {
		this.m_displayMessage = null;
	}

	/**
	 * Liefert einen Lob für einen angegebenen Artikel zurück.
	 * @param p_articleLobOid Artikel Lob OID
	 */
	public getLobByOid(p_articleLobOid: string): string {
		const params = new LobByOid.Params();
		params.lob = p_articleLobOid;
		return this.m_articleService.getLobByOid(params);
	}

	public get colClass(): string {
		switch (this.m_articles.length) {
			case 1:
			case 2:
				return "col-5";
			case 3:
				return "col-3";
			case 4:
			case 5:
				return "col-2";
		}
	}
}
